import { LiveData, Store } from '@toeverything/infra';
import {
  SuggestionBatchStatus,
  SuggestionStatus,
  type LLMBaseDataSnapshot,
  type Suggestion,
  type SuggestionBatch,
} from '../types/suggestion.d';

type DocSuggestionMap = {
  [k: string]: {
    [k: string]: SuggestionBatch;
  };
};

export type BlockMarkdownMap = {
  blockId: string;
  startLine: number; // 1-based
  endLine: number; // 1-based, inclusive
  markdown: string; // 该 block 在最终文件中的 markdown 片段
};

/**
 * ⚠️ 警告：此 Store 正在改造为只读层
 *
 * **Suggestions 数据管理：**
 * - ✅ 唯一数据源：`ySuggestionInfo` (Yjs YMap)
 * - ❌ 不要使用：`data$`, `signal$`, `removeSuggestion$` (已废弃)
 * - ❌ 不要调用：`addSuggestion()`, `updateBatchStatus()`, `resolveSuggestion()` 等写入方法
 * - ✅ 推荐：直接从 `blockSuiteDoc.ySuggestionInfo` 读取
 *
 * **LLM 基础数据管理（保留）：**
 * - `LLMBaseDataSnapshot$` - 用于存储 blockLineMapping, markdown, snapshot
 * - `modifyLLMMarkdown()`, `replaceLLMBaseDataSnapshot()` - 合法的更新方法
 */
export class SuggestionStoreBeta extends Store {
  // ❌ 已废弃：docID --> batchID -> suggestions
  // 改为从 ySuggestionInfo 读取
  private readonly data$ = new LiveData<DocSuggestionMap>({});

  // ❌ 已废弃：用于触发视图更新
  // 改为通过 ySuggestionInfo.observe() 监听
  signal$ = new LiveData<number>(0);

  // ❌ 已废弃：维护渲染到页面上的 Suggestion
  // 改为从 ySuggestionInfo 读取
  removeSuggestion$ = new LiveData<Suggestion[]>([]);

  // 维护 LLM 基础数据： 映射计算 blocks 也采用这份数据
  LLMBaseDataSnapshot$ = new LiveData<LLMBaseDataSnapshot>({
    snapshot: null,
    markdown: '',
    blockLineMapping: [],
    docId: '',
  });

  get data() {
    return this.data$.getValue();
  }

  constructor() {
    super();

    this.data$ = new LiveData<DocSuggestionMap>({});
    this.removeSuggestion$ = new LiveData<Suggestion[]>([]);

    // ⚠️ 移除全局变量引用，防止内存泄漏
    // (window as any).suggestionStore = this;
    this.signal$ = new LiveData<number>(0);
  }

  resetData() {
    this.data$.next({});
    this.removeSuggestion$.next([]);
  }

  /**
   * ❌ 已废弃：获取所有 pending 的 suggestion
   * @deprecated 请改为从 ySuggestionInfo 读取
   * @example
   * // ❌ 旧方法
   * const suggestions = suggestionStore.getAllPendingSuggestionByDocId({ doc_id });
   *
   * // ✅ 新方法（在 Service 中）
   * const ySuggestionInfo = blockSuiteDoc.ySuggestionInfo;
   * const listData = ySuggestionInfo.get('list');
   * const suggestions = (listData?.list || []).filter(s => s.status === 'pending');
   */
  getAllPendingSuggestionByDocId(params: { doc_id: string }): Suggestion[] {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);

    if (!docSuggestionMap) {
      return [];
    }

    const allSuggestions = Object.values(docSuggestionMap).reduce(
      (acc, item) => {
        acc.push(
          ...item.suggestions.filter(
            suggestion => suggestion.status === SuggestionStatus.Pending
          )
        );
        return acc;
      },
      [] as Suggestion[]
    );

    return allSuggestions;
  }

  updateRemoveSuggestion(suggestions: Suggestion[]) {
    this.removeSuggestion$.next(suggestions);
  }

  modifyLLMMarkdown(params: { markdown: string }) {
    this.LLMBaseDataSnapshot$.next({
      ...this.LLMBaseDataSnapshot$.getValue(),
      markdown: params.markdown,
    });
  }

  /**
   * ❌ 已废弃：更新批次状态
   * @deprecated 状态已由 ySuggestionInfo 管理，不需要单独更新
   */
  updateBatchStatus(params: {
    doc_id: string;
    resultMap: Map<
      string,
      Map<
        string,
        {
          suggestionId: string;
          targetBlockIds: string[];
          newBlockIds: string[];
          batch_id: string;
          doc_id: string;
          status: SuggestionStatus;
        }
      >
    >;
  }) {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);

    const updateBatchID: Set<string> = new Set();

    Object.values(docSuggestionMap).forEach(batch => {
      const suggestionStatusMap = params.resultMap.get(batch.id);

      if (suggestionStatusMap) {
        batch.suggestions.forEach(suggestion => {
          const newState = suggestionStatusMap.get(suggestion.id);
          if (newState) {
            suggestion.status = newState.status;
            suggestion.newBlockIds = newState.newBlockIds;
            suggestion.targetBlockIds = newState.targetBlockIds;
            updateBatchID.add(batch.id);
          }
        });
      }
    });

    const updateBatch: Array<{
      batch_id: string;
      suggestions: Suggestion[];
    }> = [];

    updateBatchID.forEach(batch_id => {
      const batch = docSuggestionMap[batch_id];
      if (batch) {
        updateBatch.push({
          batch_id: batch.id,
          suggestions: batch.suggestions,
        });
      }
    });

    this._updateBatch({
      doc_id: params.doc_id,
      batch: updateBatch,
    });
  }

  replaceLLMBaseDataSnapshot(params: LLMBaseDataSnapshot) {
    this.LLMBaseDataSnapshot$.next(params);
  }

  removeSuggestionByBatch(params: { doc_id: string; batch_id: string }) {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id) || {};
    const batch = docSuggestionMap[params.batch_id];

    if (!batch || batch.suggestions.length === 0) {
      return;
    }

    const removeSuggestions = batch.suggestions;

    const rest = this.removeSuggestion$.getValue();

    const deepClones = JSON.parse(JSON.stringify(removeSuggestions));

    this.updateRemoveSuggestion([...rest, ...deepClones]);

    this._removeBatchSuggestion({
      doc_id: params.doc_id,
      batch_id: params.batch_id,
    });
  }

  getAllSuggestion() {
    const docSuggestionMap = this.data$.getValue();
    const allSuggestions = Object.values(docSuggestionMap).flatMap(ite =>
      Object.values(ite)
    );

    return allSuggestions.reduce((acc, suggestion) => {
      acc.push(...suggestion.suggestions);
      return acc;
    }, [] as Suggestion[]);
  }

  // 获取所有的 suggestion
  getAllSuggestionByDocId(params: { doc_id: string }): Suggestion[] {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);

    if (!docSuggestionMap) {
      return [];
    }

    const allSuggestions = Object.values(docSuggestionMap).reduce(
      (acc, item) => {
        acc.push(...item.suggestions);
        return acc;
      },
      [] as Suggestion[]
    );

    return allSuggestions;
  }

  /**
   * 1、按照 batch 内数据进行 suggestions 更新
   * @param params
   */
  private _updateBatch(params: {
    doc_id: string;
    batch: Array<{
      batch_id: string;
      suggestions: Suggestion[];
    }>;
  }) {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);

    for (const item of params.batch) {
      const batchSuggestionMap = docSuggestionMap[item.batch_id];

      if (!batchSuggestionMap) {
        // throw new Error(`${params.doc_id} not found batch record!`);
        console.warn(`${params.doc_id} not found batch record!`);
        continue;
      }

      let hasPending =
        batchSuggestionMap.status === SuggestionBatchStatus.Pending;
      const existsMap = new Map<string, Suggestion>();
      batchSuggestionMap.suggestions.forEach(suggestion => {
        existsMap.set(suggestion.id, suggestion);
      });

      // 仅更新状态
      item.suggestions.forEach(suggestion => {
        if (existsMap.has(suggestion.id)) {
          const existsSuggestion = existsMap.get(suggestion.id);
          if (existsSuggestion) {
            existsSuggestion.status = suggestion.status;
            existsSuggestion.newBlockIds = suggestion.newBlockIds;
            existsSuggestion.targetBlockIds = suggestion.targetBlockIds;
          }
        }
      });

      batchSuggestionMap.suggestions.forEach(suggestion => {
        if (suggestion.status === SuggestionStatus.Pending) {
          hasPending = true;
        }
      });

      if (hasPending) {
        batchSuggestionMap.status = SuggestionBatchStatus.Pending;
      }

      docSuggestionMap[item.batch_id] = batchSuggestionMap;
    }

    // 更新数据
    const value = this.data$.getValue();
    value[params.doc_id] = docSuggestionMap;
    this.data$.next(value);
  }

  private _removeBatchSuggestion(params: { doc_id: string; batch_id: string }) {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);

    if (!docSuggestionMap) {
      return;
    }

    const batchSuggestionMap = docSuggestionMap[params.batch_id];

    if (!batchSuggestionMap) {
      return;
    }

    batchSuggestionMap.suggestions = [];
    batchSuggestionMap.status = SuggestionBatchStatus.Initial;

    docSuggestionMap[params.batch_id] = batchSuggestionMap;

    const value = this.data$.getValue();
    value[params.doc_id] = docSuggestionMap;
    this.data$.next(value);
  }

  // 更新 doc 的 batch 数据
  private _setDocBatchSuggestion(docId: string, batch: SuggestionBatch) {
    const oldValue = this.data$.getValue();
    const docValue = oldValue[docId] || {};

    docValue[batch.id] = batch;

    oldValue[docId] = docValue;

    this.data$.next(oldValue);
  }

  private _createDocSuggestionMap(docId: string) {
    const oldValue = this.data$.getValue();
    oldValue[docId] = {};
    this.data$.next(oldValue);
  }

  /**
   * ❌ 已废弃：添加 suggestion
   * @deprecated 请改为通过 SuggestionManager.batchCreateSuggestions() 写入 ySuggestionInfo
   */
  addSuggestion(params: {
    doc_id: string;
    batch_id: string;
    suggestions: Suggestion[];
    isInit?: boolean;
  }) {
    const docSuggestionMap = this.data$.getValue()[params.doc_id];

    if (!docSuggestionMap) {
      // throw new Error(`${params.doc_id} not found doc record!`);
      this._createDocSuggestionMap(params.doc_id);
    }

    const batchSuggestionMap = this._getSuggestionBatch(
      params.doc_id,
      params.batch_id
    );

    //
    if (batchSuggestionMap) {
      // diff 变更
      /**
       * 1、如果连续推送相同内容如何处理？ 包含同样的块，丢弃旧的，保留新的【如果还未推送到 dom 上则进行】
       * 2、同样的 Id 如何处理？
       * 3、 增量更新
       */
      const existsMap = new Map<string, Suggestion>();
      batchSuggestionMap.suggestions.forEach(suggestion => {
        existsMap.set(suggestion.id, suggestion);
      });

      const tempSuggestions: Suggestion[] = [];

      const suggestions: Suggestion[] = JSON.parse(
        JSON.stringify(params.suggestions)
      );

      suggestions.forEach(suggestion => {
        // 如果存在，则丢弃
        if (!params.isInit && existsMap.has(suggestion.id)) {
          return;
        }

        this._resolveConflict(params.doc_id, suggestion.targetBlockIds);
        tempSuggestions.push(suggestion);
      });

      // 更新 batch
      batchSuggestionMap.suggestions.push(...tempSuggestions);
      batchSuggestionMap.status = SuggestionBatchStatus.Pending;

      this._setDocBatchSuggestion(params.doc_id, batchSuggestionMap);
    } else {
      const newBatch: SuggestionBatch = {
        id: params.batch_id,
        status: SuggestionBatchStatus.Initial,
        createdAt: Date.now(),
        suggestions: [],
      };
      const tempSuggestions: Suggestion[] = [];

      params.suggestions.forEach(suggestion => {
        this._resolveConflict(params.doc_id, suggestion.targetBlockIds);

        tempSuggestions.push(suggestion);
      });

      newBatch.suggestions = tempSuggestions;

      this._setDocBatchSuggestion(params.doc_id, newBatch);
    }

    this.signal$.next(this.signal$.getValue() + 1);
  }

  createDocSuggestionMap(docID: string) {
    const value = this._findDocSuggestionMap(docID);

    if (value) {
      return;
    }

    // 更新数据
    const oldValue = this.data$.getValue();
    oldValue[docID] = {};
    this.data$.next(oldValue);
  }

  /**
   * ❌ 已废弃：处理 Suggestion ｜ 接受 ｜ 拒绝 ｜ 删除
   * @deprecated 请改为调用 SuggestionManager.applySuggestion() 或 rejectSuggestion()
   */
  resolveSuggestion(params: {
    doc_id: string;
    batch_id: string;
    suggestion_id: string;
    action: 'accept' | 'reject' | 'delete';
  }) {
    const docSuggestionMap = this._findDocSuggestionMap(params.doc_id);
    const batchSuggestionMap = docSuggestionMap[params.batch_id];

    if (!batchSuggestionMap) {
      throw new Error(`${params.doc_id} not found batch record!`);
    }

    const suggestion = batchSuggestionMap.suggestions.find(
      suggestion => suggestion.id === params.suggestion_id
    );

    if (!suggestion) {
      throw new Error(`${params.doc_id} not found suggestion record!`);
    }

    if (suggestion.status !== SuggestionStatus.Pending) {
      // throw new Error(`${params.doc_id} suggestion is not pending!`);
      console.warn(`${params.doc_id} suggestion is not pending!`);
      return;
    }

    suggestion.status =
      params.action === 'accept'
        ? SuggestionStatus.Accepted
        : params.action === 'reject'
          ? SuggestionStatus.Rejected
          : SuggestionStatus.Deleted;

    this._setDocBatchSuggestion(params.doc_id, batchSuggestionMap);

    // 🔑 修复：触发 signal$ 更新
    this.signal$.next(this.signal$.getValue() + 1);
  }

  private _findDocSuggestionMap(docId: string) {
    const all = this.data$.getValue();
    return all[docId];
  }

  private _getSuggestionBatch(docId: string, batchId: string) {
    const docSuggestionMap = this._findDocSuggestionMap(docId);
    if (docSuggestionMap) {
      return docSuggestionMap[batchId];
    }
    return null;
  }

  /**
   * 检查是否存在冲突：
   * 只处理 SuggestionBatch.status === 'pending' 的 batch，
   * 只考虑 Suggestion.status === 'pending' 的 suggestion。
   * 如果 targetBlockIds 与任何一个 pending 状态的 suggestion 存在重合，则返回 false（有冲突），否则返回 true（无冲突）。
   */
  private _resolveConflict(
    doc_id: string,
    targetBlockIds: Suggestion['targetBlockIds']
  ): Suggestion[] {
    const docSuggestionMap = this._findDocSuggestionMap(doc_id);

    const result: Suggestion[] = [];

    if (docSuggestionMap) {
      for (const batch of Object.values(docSuggestionMap)) {
        // 只处理 pending 状态的 batch
        if (
          [
            SuggestionBatchStatus.Accepted,
            SuggestionBatchStatus.Delete,
            SuggestionBatchStatus.Rejected,
          ].includes(batch.status)
        )
          continue;
        for (const suggestion of batch.suggestions) {
          // 只处理 pending 状态的 suggestion
          if (
            ![SuggestionStatus.Initial, SuggestionStatus.Pending].includes(
              suggestion.status
            )
          )
            continue;
          if (
            suggestion.targetBlockIds.some(id => targetBlockIds.includes(id))
          ) {
            // 有重合，存在冲突 | 解决冲突 ｜ 旧的标记为删除 Conflict
            suggestion.status = SuggestionStatus.Conflict;
            result.push(suggestion);
          }
        }
      }

      // 更新数据
      const newData = { ...this.data$.getValue() };
      newData[doc_id] = docSuggestionMap;
      this.data$.next(newData);
    }

    if (result.length) {
      this.removeSuggestion$.next(result);
    }

    //
    return result;
  }
}
